import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchCurrentUser, login as apiLogin, logout as apiLogout, type SuperAdminUser } from "@/lib/api";

/**
 * Auth state for the platform super-admin SPA. Mirrors the operator-side
 * AuthProvider pattern but the principal is a SuperAdminUser (no tenant
 * slug; role is always "super_admin").
 */
interface AuthContextValue {
  user: SuperAdminUser | null | undefined;
  login: (email: string, password: string) => Promise<SuperAdminUser>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SuperAdminUser | null | undefined>(undefined);

  const refresh = useCallback(async () => {
    const me = await fetchCurrentUser();
    setUser(me);
  }, []);

  useEffect(() => {
    refresh().catch((err) => {
      console.error("auth.refresh failed", err);
      setUser(null);
    });
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const me = await apiLogin(email, password);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, login, logout, refresh }),
    [user, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside an AuthProvider");
  return ctx;
}
